import { useEffect, useState } from 'react'
import { Link, createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { supabase } from '../lib/supabase'

/**
 * Where the magic link lands.
 *
 * The supabase client is configured with `detectSessionInUrl: true`, so by the
 * time this component runs it is already busy turning whatever the link put in
 * the URL into a stored session. We just wait for that to finish.
 */
export const Route = createFileRoute('/auth/callback')({
  // The return type marks `redirect` OPTIONAL, which is what lets a plain
  // <Link to="/login"> compile without supplying search params.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: AuthCallbackPage,
})

function AuthCallbackPage() {
  const navigate = useNavigate()
  const router = useRouter()
  const { redirect } = Route.useSearch()
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    let cancelled = false

    function finish() {
      if (redirect) {
        // history.replace, NOT navigate({ to }). `to` is a typed PATHNAME, so a
        // stored href like "/checkout?date=2026-08-20&start=09:10" would be
        // treated as one literal segment and 404. replace (not push) also keeps
        // the token-bearing callback URL out of the back stack.
        router.history.replace(redirect)
      } else {
        void navigate({ to: '/me', replace: true })
      }
    }

    // Fires as soon as the session is established from the URL.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session && !cancelled) finish()
    })

    // It may also already be done before we subscribed.
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session && !cancelled) finish()
    })

    const timer = setTimeout(() => !cancelled && setTimedOut(true), 8000)

    return () => {
      cancelled = true
      clearTimeout(timer)
      subscription.subscription.unsubscribe()
    }
  }, [navigate, router, redirect])

  if (timedOut) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Ese enlace no funcionó</h1>
        <p className="text-slate-600">
          Puede haber vencido o ya haberse usado. Los enlaces sirven una sola vez.
        </p>
        <Link to="/login" search={{ redirect: undefined }} className="text-slate-900 underline">
          Request a new one
        </Link>
      </div>
    )
  }

  return <p className="text-slate-600">Iniciando sesión…</p>
}
